import { Spinner, Stack } from 'component-library';
import { useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { SuspenseBoundary } from '@/components';
import { SearchBar } from '@/components/widgets/m/Search/SearchBar';
import { Card } from '@/components/widgets/o/Card/Card';
import { useDebounce } from '@/hooks/useDebounce';
import { useWorkstreams } from '@/hooks/useWorkstreams';
import { Logger } from '@/lib/logs/logger';
import { WorkstreamCard } from '@/pages/shared/home/components/WorkstreamCard';
import { WorkstreamEditCard } from '@/pages/shared/home/components/WorkstreamEditCard';
import { isNullish } from '@/utils/booleans';

type WorkstreamsProps = {
  isEditView?: boolean;
  hasSearch?: boolean;
  hasCreate?: boolean;
};

export const Workstreams = ({ isEditView = false, hasSearch = false, hasCreate = false }: WorkstreamsProps) => {
  const { t } = useTranslation();

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <Card
          styles={{
            root: 'rounded-3xl p-4',
          }}
        >
          <Stack spacing={4}>
            <span className='typography-body1'>{t('global.workstream.labels.workstreams')}</span>
            <span className='typography-body1 text-text-disabled'>{t('global.widget.error.message')}</span>
          </Stack>
        </Card>
      )}
      onError={(error, info) => {
        Logger.error(`Workstreams rendering error: ${error} ${info}`);
      }}
    >
      <SuspenseBoundary
        fallback={
          <Stack centerMain>
            <Spinner size={10} />
          </Stack>
        }
      >
        <WorkstreamsBase isEditView={isEditView} hasSearch={hasSearch} hasCreate={hasCreate} />
      </SuspenseBoundary>
    </ErrorBoundary>
  );
};

const WorkstreamsBase = ({ isEditView, hasSearch }: WorkstreamsProps) => {
  const { t } = useTranslation();
  const workstreams = useWorkstreams().data;

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  const filteredWorkstreams = useMemo(() => {
    if (isNullish(debouncedSearchQuery.trim())) return workstreams;

    const queryString = debouncedSearchQuery.toLowerCase();

    return workstreams.filter(
      (workstream) =>
        workstream.title.trim().toLowerCase().includes(queryString) ||
        workstream?.description?.trim().toLowerCase().includes(queryString),
    );
  }, [workstreams, debouncedSearchQuery]);

  return (
    <Stack spacing={4}>
      <Stack className='gap-2 flex flex-row justify-between'>
        <span className='typography-overline text-text-secondary'>
          {t('global.workstream.labels.workstreamCount', { count: filteredWorkstreams.length })}
        </span>
        {hasSearch && <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />}
      </Stack>
      <Stack spacing={4} className='gap-4 justify-start'>
        {filteredWorkstreams.map((workstream, i) =>
          isEditView ? (
            <WorkstreamEditCard key={`workstream-${i}`} workstream={workstream} />
          ) : (
            <WorkstreamCard key={`workstream-${i}`} workstream={workstream} />
          ),
        )}
      </Stack>
    </Stack>
  );
};
